import React, { useState, useEffect } from 'react';
import './WeatherPage.css';

function WeatherPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCelsius, setIsCelsius] = useState(true);
  const [currentWeather, setCurrentWeather] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [cityName, setCityName] = useState('Rarotonga, Cook Islands');

  const COOK_ISLANDS_LAT = -21.2367;
  const COOK_ISLANDS_LON = -159.7777;

  useEffect(() => {
    fetchWeather();
  }, []);

  const fetchWeather = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const url = `https://api.open-meteo.com/v1/forecast?latitude=${COOK_ISLANDS_LAT}&longitude=${COOK_ISLANDS_LON}&current=temperature_2m,apparent_temperature,weather_code,wind_speed_10m,relative_humidity_2m,is_day&daily=weather_code,temperature_2m_max,temperature_2m_min&timezone=auto&forecast_days=7`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch weather data');
      
      const data = await response.json();
      
      setCurrentWeather({
        temperature: data.current.temperature_2m,
        apparentTemp: data.current.apparent_temperature,
        weatherCode: data.current.weather_code,
        windSpeed: data.current.wind_speed_10m,
        humidity: data.current.relative_humidity_2m,
        isDay: data.current.is_day === 1
      });

      const dailyForecast = data.daily.time.map((date, index) => ({
        date: new Date(date),
        weatherCode: data.daily.weather_code[index],
        maxTemp: data.daily.temperature_2m_max[index],
        minTemp: data.daily.temperature_2m_min[index]
      }));

      setForecast(dailyForecast);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTemp = (celsius) => {
    if (isCelsius) {
      return `${Math.round(celsius)}°C`;
    }
    const fahrenheit = (celsius * 9/5) + 32;
    return `${Math.round(fahrenheit)}°F`;
  };

  const getWeatherIcon = (code, isDay) => {
    if (code === 0) return isDay ? '☀️' : '🌙';
    if ([1, 2, 3].includes(code)) return isDay ? '⛅' : '☁️';
    if ([45, 48].includes(code)) return '🌫️';
    if ([51, 53, 55, 56, 57].includes(code)) return '🌧️';
    if ([61, 63, 65, 66, 67].includes(code)) return '🌧️';
    if ([71, 73, 75, 77, 85, 86].includes(code)) return '❄️';
    if ([80, 81, 82].includes(code)) return '🌧️';
    if ([95, 96, 99].includes(code)) return '⛈️';
    return '☁️';
  };

  const getWeatherLabel = (code) => {
    if (code === 0) return 'Clear';
    if ([1, 2, 3].includes(code)) return 'Partly Cloudy';
    if ([45, 48].includes(code)) return 'Foggy';
    if ([51, 53, 55].includes(code)) return 'Drizzle';
    if ([61, 63, 65].includes(code)) return 'Rain';
    if ([66, 67].includes(code)) return 'Freezing Rain';
    if ([71, 73, 75, 77].includes(code)) return 'Snow';
    if ([80, 81, 82].includes(code)) return 'Rain Showers';
    if ([95, 96, 99].includes(code)) return 'Thunderstorm';
    return 'Cloudy';
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="weather-page">
        <div className="weather-loading">
          <div className="spinner"></div>
          <p>Loading weather data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="weather-page">
        <div className="weather-error">
          <h2>⚠️ Error</h2>
          <p>{error}</p>
          <button onClick={fetchWeather} className="retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="weather-page">
      <div className="weather-header">
        <h1>{cityName}</h1>
        <button 
          className="temp-toggle-btn"
          onClick={() => setIsCelsius(!isCelsius)}
          title={isCelsius ? 'Switch to °F' : 'Switch to °C'}
        >
          {isCelsius ? '°C' : '°F'}
        </button>
      </div>

      {currentWeather && (
        <div className="current-weather-card">
          <div className="current-weather-main">
            <div className="weather-icon-large">
              {getWeatherIcon(currentWeather.weatherCode, currentWeather.isDay)}
            </div>
            <div className="current-weather-details">
              <div className="weather-condition">{getWeatherLabel(currentWeather.weatherCode)}</div>
              <div className="temperature-display">
                <span className="temp-value">{Math.round(currentWeather.temperature)}</span>
                <span className="temp-unit">{isCelsius ? '°C' : '°F'}</span>
              </div>
              <div className="weather-meta">
                <span className="meta-item">
                  <span className="meta-icon">💧</span>
                  Humidity {Math.round(currentWeather.humidity)}%
                </span>
                <span className="meta-item">
                  <span className="meta-icon">💨</span>
                  Wind {Math.round(currentWeather.windSpeed)} km/h
                </span>
              </div>
              <div className="feels-like">
                Feels like {formatTemp(currentWeather.apparentTemp)}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="forecast-section">
        <h2>7-Day Forecast</h2>
        <div className="forecast-grid">
          {forecast.map((day, index) => (
            <div key={index} className="forecast-card">
              <div className="forecast-day">{formatDate(day.date)}</div>
              <div className="forecast-icon">{getWeatherIcon(day.weatherCode, true)}</div>
              <div className="forecast-condition">{getWeatherLabel(day.weatherCode)}</div>
              <div className="forecast-temps">
                <span className="temp-high">{formatTemp(day.maxTemp)}</span>
                <span className="temp-divider">·</span>
                <span className="temp-low">{formatTemp(day.minTemp)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="weather-attribution">
        <p>Weather data provided by <a href="https://open-meteo.com/" target="_blank" rel="noopener noreferrer">Open-Meteo</a></p>
      </div>
    </div>
  );
}

export default WeatherPage;
